import React, { useState, useEffect, useContext } from "react";
import { useTranslation } from "react-i18next";
import { UserContext } from "../../UserContext";
import axios from "axios";
import "./FieldSelectorModal.css";
import toast from "react-hot-toast";

const FieldSelectorModal = ({ isOpen, onClose, onFieldsSelected, multiSelect = false, selectedFieldIds = [] }) => {
  const { t, i18n } = useTranslation();
  const { userData, token } = useContext(UserContext);
  const [fields, setFields] = useState([]);
  const [selectedFields, setSelectedFields] = useState(new Set(selectedFieldIds));
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  // Detect RTL direction
  const isRTL = i18n.language === 'ar';

  useEffect(() => {
    if (isOpen) {
      fetchFields();
      setSelectedFields(new Set(selectedFieldIds));
    }
  }, [isOpen, selectedFieldIds]);

  const fetchFields = async () => {
    if (!token || !userData?.Farm?.id) return;

    setLoading(true);
    try {
      // Using correct API endpoint: /api/field/findByFarmId/{farmId}
      const response = await axios.get(`/api/field/findByFarmId/${userData.Farm.id}`, {
        headers: { Authorization: token }
      });

      // API returns {items: fields} structure
      const fieldsData = response.data?.items || response.data || [];
      setFields(Array.isArray(fieldsData) ? fieldsData : []);
    } catch (error) {
      console.error("Error fetching fields:", error);
      toast.error(t("Failed to load fields"));
      setFields([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFieldToggle = (field) => {
    const newSelection = new Set(selectedFields);
    
    if (!multiSelect) {
      // Single select mode - clear selection and add new field
      newSelection.clear();
      newSelection.add(field.id);
    } else {
      // Multi-select mode - toggle field
      if (newSelection.has(field.id)) {
        newSelection.delete(field.id);
      } else {
        newSelection.add(field.id);
      }
    }
    
    setSelectedFields(newSelection);
  };

  const handleToggleAll = () => {
    if (multiSelect) {
      if (selectedFields.size === fields.length) {
        // All selected - deselect all
        setSelectedFields(new Set());
      } else {
        // Not all selected - select all
        const allFieldIds = fields.map(field => field.id);
        setSelectedFields(new Set(allFieldIds));
      }
    }
  };

  const handleApply = () => {
    const selected = fields.filter(field => selectedFields.has(field.id));
    onFieldsSelected(selected);
    onClose();
  };

  const handleCancel = () => {
    setSelectedFields(new Set(selectedFieldIds));
    setSearchTerm("");
    onClose();
  };

  const filteredFields = fields.filter(field => {
    const fieldName = (field.name || field.fieldName || '').toLowerCase();
    const cropInfo = (field.cropType || field.cropName || '').toLowerCase();
    const search = searchTerm.toLowerCase();
    return fieldName.includes(search) || cropInfo.includes(search);
  });

  if (!isOpen) return null;

  return (
    <div className="field-selector-modal-overlay" onClick={handleCancel}>
      <div className="field-selector-modal" dir={isRTL ? 'rtl' : 'ltr'} onClick={(e) => e.stopPropagation()}>
        <div className="field-selector-header">
          <h2>{t("Select Fields")}</h2>
          <button className="close-button" onClick={handleCancel}>×</button>
        </div>

        <div className="field-selector-search">
          <input
            type="text"
            placeholder={t("Search fields...")}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
          {multiSelect && filteredFields.length > 0 && (
            <button
              className={`toggle-all-button ${selectedFields.size === filteredFields.length ? 'all-selected' : ''}`}
              onClick={handleToggleAll}
            >
              {selectedFields.size === filteredFields.length ? t("Deselect All") : t("Select All")}
            </button>
          )}
        </div>

        <div className="field-selector-content">
          {loading ? (
            <div className="loading-message">{t("Loading fields...")}</div>
          ) : filteredFields.length === 0 ? (
            <div className="no-fields-message">{t("No fields available")}</div>
          ) : (
            <div className="fields-list">
              {filteredFields.map(field => (
                <div 
                  key={field.id} 
                  className={`field-item ${selectedFields.has(field.id) ? 'selected' : ''}`}
                  onClick={() => handleFieldToggle(field)}
                >
                  <div className="field-checkbox">
                    <input
                      type={multiSelect ? "checkbox" : "radio"}
                      checked={selectedFields.has(field.id)}
                      onChange={() => {}}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                  <div className="field-info">
                    <div className="field-name">{field.name || field.fieldName}</div>
                    {(field.cropType || field.cropName) && (
                      <div className="field-crop">{field.cropType || field.cropName}</div>
                    )}
                    {field.area && (
                      <div className="field-area">
                        {field.area} {field.areaUnit || field.unit || 'ha'}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="field-selector-footer">
          <div className="selected-count">
            {selectedFields.size > 0 ? (
              <span>
                {selectedFields.size} {t("fields selected")}
              </span>
            ) : (
              <span>{t("Select one or more fields for cost distribution")}</span>
            )}
          </div>
          <div className="footer-buttons">
            <button
              className="cancel-button"
              onClick={handleCancel}
            >
              {t("Cancel")}
            </button>
            <button
              className="apply-button"
              onClick={handleApply}
              disabled={selectedFields.size === 0}
            >
              {t("Apply")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FieldSelectorModal;